/**
 * Las organizaciones del usuario, cargadas una sola vez y compartidas por las pantallas que las
 * necesitan (docs/FASE-7.md §7, ADR-007).
 *
 * Vive fuera de `pages/` porque ya no es dato de una pantalla: «Mis módulos» las pinta, «Usuarios»
 * elige entre ellas, y la barra superior decide con `canManageMembers` si esa pestaña existe. Que
 * cada una lo pidiera por su cuenta significaría tres respuestas distintas del mismo endpoint
 * conviviendo en la misma sesión, y una invitación aceptada que solo se ve en una de ellas.
 *
 * **Solo pide con la sesión ya autenticada.** `getFreshAccessToken()` devuelve `null` mientras el
 * arranque de `auth-web` no ha terminado, y `useAuthorizedFetch` manda la petición igual, sin
 * cabecera: el núcleo responde 401 «Falta el encabezado Authorization». Antes de subir esta carga a
 * `App` el problema no existía, porque la pantalla que la hacía solo se montaba después de
 * autenticar; al subirla quedó por encima de esa condición y pasó a dispararse en el primer render.
 * Y como el efecto no se repetía al autenticar, el error se quedaba fijo en pantalla para siempre.
 */

import { useAuth, useAuthorizedFetch } from "@continnua/auth-web/react";
import type { MeOrgsEntry } from "@continnua/contracts";
import { useCallback, useEffect, useState } from "react";

import { api } from "./api";

export interface EstadoOrganizaciones {
  /** `null` mientras carga; `[]` es una respuesta válida (usuario sin organizaciones). */
  orgs: MeOrgsEntry[] | null;
  error: string | null;
  /** Vuelve a pedir la lista. La usa «Usuarios» tras invitar o quitar a alguien. */
  recargar: () => void;
}

/**
 * Si tiene sentido pedir datos con esta sesión. Función pura y exportada para poder fijarla en una
 * prueba: es una condición de una línea, pero es exactamente la que faltaba.
 */
export function puedePedir(status: string): boolean {
  return status === "authenticated";
}

export function useOrganizaciones(): EstadoOrganizaciones {
  const { status } = useAuth();
  const authorizedFetch = useAuthorizedFetch();
  const [orgs, setOrgs] = useState<MeOrgsEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [intento, setIntento] = useState(0);

  useEffect(() => {
    // `status` está en las dependencias a propósito: es lo que hace que la carga se lance sola al
    // terminar el arranque, en vez de quedarse esperando a que algo la reintente.
    if (!puedePedir(status)) return;

    let vivo = true;
    api
      .orgs(authorizedFetch)
      .then((respuesta) => {
        if (!vivo) return;
        setError(null);
        setOrgs([...respuesta.orgs].sort((a, b) => Number(b.isDefault) - Number(a.isDefault)));
      })
      .catch((err: unknown) => {
        if (vivo) setError(err instanceof Error ? err.message : "No se pudieron cargar tus organizaciones.");
      });
    return () => {
      vivo = false;
    };
  }, [authorizedFetch, intento, status]);

  return { orgs, error, recargar: useCallback(() => setIntento((n) => n + 1), []) };
}
